// ===========================================
// Outstanding Invoices Page
// ===========================================
// Dedicated page for viewing and managing outstanding invoices

import { Box, Typography, CircularProgress, Card, CardContent, Grid } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

import {
  useOutstandingInvoices,
  useExportOutstandingInvoices,
  formatCurrency,
} from '../../../hooks/useReports';
import PageHeader from '../../../components/common/PageHeader';
import OutstandingInvoicesTable from '../../../components/reports/OutstandingInvoicesTable';
import ExportButtons from '../../../components/reports/ExportButtons';
import { StatWidget } from '../../../components/dashboard/StatWidget';

// ===========================================
// HELPERS
// ===========================================

/**
 * Get aging bucket totals from invoices
 */
function getAgingBucketTotals(invoices: any[]) {
  const buckets = {
    '0-30': 0,
    '31-60': 0,
    '61-90': 0,
    '90+': 0,
  };

  invoices.forEach((invoice) => {
    const daysOverdue = invoice.daysOverdue;
    if (daysOverdue <= 30) buckets['0-30'] += invoice.amountDue;
    else if (daysOverdue <= 60) buckets['31-60'] += invoice.amountDue;
    else if (daysOverdue <= 90) buckets['61-90'] += invoice.amountDue;
    else buckets['90+'] += invoice.amountDue;
  });

  return buckets;
}

// ===========================================
// COMPONENT
// ===========================================

export default function OutstandingInvoicesPage() {
  const { data: invoices = [], isLoading } = useOutstandingInvoices();
  const exportMutation = useExportOutstandingInvoices();

  const handleExport = (format: 'csv' | 'pdf') => {
    exportMutation.mutate(format);
  };

  // Calculate aging bucket totals
  const agingBuckets = getAgingBucketTotals(invoices);
  const totalOutstanding = invoices.reduce((sum, inv) => sum + inv.amountDue, 0);

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="Outstanding Invoices"
        subtitle="View all unpaid and overdue invoices"
        icon={<WarningIcon />}
        action={
          invoices.length > 0 && (
            <ExportButtons
              onExport={handleExport}
              loading={exportMutation.isPending}
              disabled={invoices.length === 0}
            />
          )
        }
      />

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Content */}
      {!isLoading && (
        <>
          {/* Aging Bucket Summary Cards */}
          {invoices.length > 0 && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatWidget
                  title="0-30 Days"
                  value={formatCurrency(agingBuckets['0-30'])}
                  icon={<WarningIcon />}
                  color="success"
                  subtitle="Current"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatWidget
                  title="31-60 Days"
                  value={formatCurrency(agingBuckets['31-60'])}
                  icon={<WarningIcon />}
                  color="warning"
                  subtitle="Moderately overdue"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatWidget
                  title="61-90 Days"
                  value={formatCurrency(agingBuckets['61-90'])}
                  icon={<WarningIcon />}
                  color="error"
                  subtitle="Seriously overdue"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatWidget
                  title="90+ Days"
                  value={formatCurrency(agingBuckets['90+'])}
                  icon={<WarningIcon />}
                  color="error"
                  subtitle="Critically overdue"
                />
              </Grid>
            </Grid>
          )}

          {/* Outstanding Invoices Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                All Outstanding Invoices ({invoices.length})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total Outstanding: {formatCurrency(totalOutstanding)}
              </Typography>
              <OutstandingInvoicesTable invoices={invoices} loading={isLoading} />
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
